import type { RPhoneNumber, RWhatsappTemplate } from 'v2/types/api/settings'
import { COUNTRIES_WITH_MMS, COUNTRIES_WITH_SMS } from 'v2/configs/constants'
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import type { InitiateWhatsAppConversationPayload } from 'v2/types/api/conversations'

export function getHeaderFormatName(format: string): string {
  switch (format.toLowerCase()) {
    case 'image':
      return 'Image'
    case 'document':
      return 'Document'
    case 'video':
      return 'Video'
    default:
      return format
  }
}

export function getShortBody(text: string, maxLength = 50): string {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

export const getHeaderObject = (type: string, text: string) => {
  switch (type) {
    case 'IMAGE':
      return { type: 'image', image: { link: text } }
    case 'DOCUMENT':
      return { type: 'document', document: { link: text } }
    case 'VIDEO':
      return { type: 'video', video: { link: text } }
    case 'TEXT':
      return { type: 'text', text }
    default:
      return { type: 'text', text: '' }
  }
}

interface ValidationResult {
  isSMSEnabled: boolean
  isMMSEnabled: boolean
}

export const validateSMSNumber = (from?: RPhoneNumber, to?: string): ValidationResult => {
  if (!from || !to) return { isSMSEnabled: false, isMMSEnabled: false }

  if (!isValidPhoneNumber(to)) return { isSMSEnabled: false, isMMSEnabled: false }

  const toPhoneNumber = parsePhoneNumber(to)
  const toCountry = toPhoneNumber?.country

  if (!toCountry) return { isSMSEnabled: false, isMMSEnabled: false }

  if (from.is_sender_id) {
    const fromCountries = from.countries.map((c) => c.country_code)

    return {
      isSMSEnabled: fromCountries.some(
        (code) =>
          COUNTRIES_WITH_SMS.includes(code) &&
          COUNTRIES_WITH_SMS.includes(toCountry) &&
          fromCountries.includes(toCountry),
      ),
      isMMSEnabled: fromCountries.some(
        (code) =>
          COUNTRIES_WITH_MMS.includes(code) &&
          COUNTRIES_WITH_MMS.includes(toCountry) &&
          fromCountries.includes(toCountry),
      ),
    }
  }

  if (!isValidPhoneNumber(from.number)) return { isSMSEnabled: false, isMMSEnabled: false }

  const fromPhoneNumber = parsePhoneNumber(from.number)
  const fromCountry = fromPhoneNumber?.country

  if (!fromCountry) return { isSMSEnabled: false, isMMSEnabled: false }

  return {
    isSMSEnabled:
      COUNTRIES_WITH_SMS.includes(fromCountry) &&
      COUNTRIES_WITH_SMS.includes(toCountry) &&
      fromCountry === toCountry,
    isMMSEnabled:
      COUNTRIES_WITH_MMS.includes(fromCountry) &&
      COUNTRIES_WITH_MMS.includes(toCountry) &&
      fromCountry === toCountry,
  }
}

export function generateWhatsAppPayload({
  template,
  variables,
  from,
  to,
}: {
  template: RWhatsappTemplate
  variables: { header: string[]; body: string[] }
  from: string
  to: string
}): InitiateWhatsAppConversationPayload {
  return {
    from,
    to,
    template_attributes: {
      components: [
        {
          type: 'header',
          parameters: [
            getHeaderObject(
              template.data.components.find((c) => c.type === 'HEADER')?.format || '',
              variables.header[0],
            ) as { type: string; text: string },
          ],
        },
        {
          type: 'body',
          parameters: variables.body.map((text) => ({ type: 'text', text })),
        },
      ].filter((c) =>
        c.type === 'header' ? variables.header.length > 0 : variables.body.length > 0,
      ),
      language: {
        policy: 'deterministic',
        code: template.data.language,
      },
      name: template.data.name,
      namespace: template.data.namespace,
      template_uuid: template.uuid,
    },
  }
}
